/**
 * persistOAuthToken — the single token-persistence step shared by BOTH OAuth
 * completion paths (popup and inline/full-page).
 *
 * A successful social sign-in mints a JWT that the frontend must persist the
 * same way a normal email/password login does:
 *   1. setAuthToken(token, rememberMe) — writes it under 'gdb_token' (the
 *      localStorage key the api-client reads as its Bearer header) AND hands it
 *      off to the local sidecar so the sidecar can make its own authed cloud
 *      calls (downloads). OAuth has no rememberMe checkbox, so we default to
 *      true (stay signed in), matching the email/password default.
 *   2. refresh auth state via invalidateAuth(queryClient) so the useAuth()
 *      status query re-runs, sends the new Bearer to /api/auth/status, and
 *      React mirrors the canonical session.
 *
 * Both the inline OAuthCompletePage (full-page redirect landing) and the
 * useOAuthSignIn popup hook route through here so there is exactly ONE place
 * that persists + hands off + invalidates — they cannot diverge.
 */

import type { QueryClient } from '@tanstack/react-query'
import { invalidateAuth } from './invalidateAuth'
import { setAuthToken, TOKEN_KEY } from './setAuthToken'

/** localStorage key for the Bearer JWT. Must match lib/api-client.ts. */
export { TOKEN_KEY }

/**
 * Persist a freshly minted OAuth JWT (+ sidecar hand-off) and refresh
 * auth-derived state. Mirrors how the email/password login persists a token.
 */
export async function persistOAuthToken(
  queryClient: QueryClient,
  token: string,
): Promise<void> {
  // OAuth has no rememberMe control — default to true (stay signed in).
  setAuthToken(token, true)
  await invalidateAuth(queryClient)
}
